import React, { FC } from 'react';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

interface AvailableLanguage {
  href: string;
  label: string;
}

interface Props {
  availableLanguages: AvailableLanguage[];
}

const EsVersion: FC<Props> = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='politica-de-biometria-y-renuncia-a-reclamaciones'>
        Política de biometría y renuncia a reclamaciones
      </h4>

      <p>Idiomas disponibles:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Última actualización: 20 de febrero de 2026</p>
      <p>
        La Política de biometría y renuncia a reclamaciones de Plaid ("Política
        de biometría") describe cómo Plaid Inc. y sus subsidiarias, Plaid
        Financial Ltd. y Plaid, B.V. (en conjunto, "Plaid") tratan las
        fotografías o imágenes de video de su rostro, incluidos los escaneos de
        geometría facial extraídos a partir de esas imágenes, que pueden
        considerarse datos biométricos en algunas jurisdicciones.
      </p>
      <p>
        Plaid actúa como proveedor de servicios para nuestros clientes al
        prestar servicios de verificación de identidad y prevención de fraudes.
        Para prestar nuestros servicios de verificación de identidad o
        prevención de fraudes, nuestros clientes pueden exigirle que proporcione
        a Plaid o a los proveedores de servicios de Plaid un documento de
        identidad que contenga su imagen o una fotografía o imagen de video de
        usted mismo(a).
      </p>
      <p>
        Plaid o los proveedores de servicios de Plaid recopilan y procesan la
        imagen de su documento de identidad y su fotografía o imagen de video,
        según las instrucciones de nuestro cliente, para obtener un escaneo de
        su geometría facial utilizando tecnologías de reconocimiento facial o
        tecnologías similares y así poder proporcionarles a nuestros clientes
        servicios de verificación de identidad y prevención de fraudes. Plaid o
        los proveedores de servicios de Plaid también pueden procesar y usar su
        fotografía o imagen de video para servicios de detección y prevención de
        fraudes, incluso para determinar si su fotografía o imagen de video
        representa a una persona real. La imagen de su documento de identidad,
        su fotografía o imagen de video, y cualquier escaneo de geometría facial
        obtenida a partir de estas imágenes, pueden considerarse información
        biométrica en algunas jurisdicciones.
      </p>
      <p>
        Plaid o los proveedores de servicios de Plaid recopilarán, procesarán,
        obtendrán, usarán y almacenarán la imagen de su documento de identidad,
        fotografía o su imagen de video para los siguientes propósitos:
      </p>
      <ol>
        <li>
          comparar su geometría facial obtenida a partir de la imagen en su
          documento de identidad con su geometría facial obtenida en la
          fotografía o imagen de video que proporcione para verificar su
          identidad;
        </li>

        <li>
          detectar y prevenir fraudes, lo cual pueden hacer mediante la
          determinación de su edad estimada, si corresponde, utilizando una
          imagen de su documento de identidad, una imagen de una fotografía o
          imagen de video de usted mismo(a), o su geometría facial obtenida, y
        </li>

        <li>
          mantener o prestar nuestros servicios a nuestros clientes, lo que
          incluye ayudar a nuestros clientes a revisar las sesiones de
          verificación de identidad.
        </li>
      </ol>
      <p>
        La imagen de su documento de identidad, fotografía o imágenes de video
        de su rostro, y su geometría facial obtenida, si corresponde, son
        almacenadas en un formato cifrado por Plaid y los proveedores de
        servicios de Plaid en nombre de los clientes de Plaid.
      </p>
      <p>
        PLAID Y LOS PROVEEDORES DE SERVICIOS DE PLAID NUNCA UTILIZAN LA
        INFORMACIÓN PERSONAL NI LA IMAGEN EXTRAÍDA DE SU DOCUMENTO DE IDENTIDAD,
        FOTOGRAFÍA O IMAGEN DE VIDEO, INCLUIDO EL ESCANEO DE GEOMETRÍA FACIAL
        OBTENIDA QUE ESTÉ ASOCIADO CON SUS IMÁGENES, PARA MEJORAR, PERFECCIONAR
        O DESARROLLAR NUESTROS SERVICIOS.
      </p>
      <p>
        Si no proporciona un documento de identidad con una imagen de su rostro
        o una fotografía y una imagen de video de usted mismo(a), Plaid y los
        proveedores de servicios de Plaid no procesarán sus imágenes para
        obtener sus datos de geometría facial que permitirán comparar su
        documento de identidad con su fotografía o imagen de video. Plaid
        conservará un registro de su proceso de verificación parcialmente
        completado de acuerdo con las instrucciones del cliente, a menos que la
        ley o un proceso legal exijan eliminar dichos datos después de un
        período de tiempo más corto. Para obtener más información, consulte
        nuestra{' '}
        <Link href='#idv-privacy-statement'>
          <a>Declaración de privacidad de IDV</a>
        </Link>
        .
      </p>
      <p>
        <strong>Retención y almacenamiento</strong>
      </p>
      <p>
        Cuando Plaid actúa como proveedor de servicios, según lo define la ley
        aplicable, nuestros clientes le indican a Plaid que procese, almacene,
        conserve o elimine su documento de identidad o su fotografía o imagen de
        video. Plaid y los proveedores de servicios de Plaid destruirán y
        eliminarán de manera permanente cualquier documento de identidad o
        cualquier fotografía o imagen de video de acuerdo con las instrucciones
        de nuestro cliente. Plaid almacenará los documentos de identidad o las
        fotografías o imágenes de video que usted proporcione durante la
        relación comercial entre Plaid y nuestro cliente para la cual se
        recopilaron y conservaron las imágenes, a menos que una ley o un proceso
        legal exijan eliminar dichos datos después de un período más corto.
      </p>
      <p>
        Si ha creado una Cuenta de Plaid sujeta a los "{' '}
        <Link href='https://plaid.com/legal/#plaid-account-terms'>
          <a>Plaid Account Terms</a>
        </Link>
        " y ha guardado su información de verificación de identidad, Plaid puede
        almacenar, según sus instrucciones, uno o más documentos de identidad
        que contengan imágenes faciales que usted proporcione. Plaid nunca
        procesa la imagen de estos documentos de identidad para ningún propósito
        adicional —a menos que usted se lo indique— cuando usted realiza un
        proceso de verificación con una nueva aplicación o servicio, ni Plaid
        obtiene sus datos de geometría facial a partir de los documentos de
        identidad almacenados en su Cuenta de Plaid. Puede obtener más
        información sobre cómo eliminar sus documentos de identidad almacenados
        en su Cuenta de Plaid comunicándose con{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        o creando una cuenta en el portal de Plaid visitando{' '}
        <Link href='https://my.plaid.com/'>
          <a>my.plaid.com</a>
        </Link>
        . Sin perjuicio de cualquier indicación que establezca algo diferente en
        la{' '}
        <Link href='https://plaid.com/legal/#end-user-privacy-policy'>
          <a>Política de privacidad aplicable al usuario final de Plaid</a>
        </Link>{' '}
        o en los "{' '}
        <Link href='https://plaid.com/legal/#plaid-account-terms'>
          <a>Plaid Account Terms</a>
        </Link>
        ", esta Política de biometría prevalecerá en caso de cualquier
        incongruencia con otros términos o avisos que se le proporcionen.
      </p>
      <p>
        <strong>Términos adicionales</strong>
      </p>
      <p>
        Plaid o los proveedores de servicios de Plaid solo usan y procesan la
        imagen de su documento de identidad o su fotografía o imagen de video de
        acuerdo con esta Política de biometría, y por ningún otro motivo. Plaid
        no vende información biométrica. Plaid no es responsable de las
        políticas o prácticas de sus clientes. Consulte la{' '}
        <Link href='#idv-privacy-statement'>
          <a>Declaración de privacidad de IDV</a>
        </Link>{' '}
        de Plaid para comprender cómo recopilamos y utilizamos su Información
        personal en nombre de nuestros clientes. Plaid le aconseja que también
        revise las políticas de privacidad de nuestros clientes y cualquier
        término y condición correspondiente que pueda aplicarse al uso de sus
        datos, algunos de los cuales pueden considerarse datos biométricos en su
        región.
      </p>
      <p>
        Al utilizar los servicios de verificación de identidad y prevención de
        fraudes de Plaid para conectarse a una aplicación o servicio
        proporcionado por nuestros clientes, acepta que cualquier documento de
        identidad o cualquier fotografía o imagen de video que proporcione para
        verificar su identidad con nuestros clientes: (1) son auténticos; (2) en
        cuanto a los documentos de identidad presentados, son sus propios
        documentos de identidad emitidos por el gobierno, y (3) en cuanto a
        cualquier imagen de documento de identidad, fotografía o imagen de video
        presentadas, estas representan con precisión su rostro y solo su rostro
        en la fotografía o imagen de video que proporcione.
      </p>
      <p>
        Usted acepta que ha leído, comprende y acepta voluntariamente esta
        Política de biometría y renuncia a reclamaciones; renuncia en beneficio
        de Plaid y los proveedores de servicios de Plaid a cualquier reclamación
        relacionada con la recopilación, procesamiento, uso, divulgación o
        intercambio de su documento de identidad y su fotografía o imagen de
        video, incluidos los datos obtenidos a partir de estas imágenes para
        crear un escaneo de geometría facial, y usted confirma que no está
        accediendo a los servicios de Plaid en ninguna jurisdicción donde esos
        servicios no estén permitidos por la ley aplicable.
      </p>
      <hr className='section-divider' />
    </>
  );
};

export default EsVersion;
